import * as React from 'react';
import {
  DesignerStores,
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerFlux,
} from '@packages/systems/designer/flux';
import {
  MCC_ALL_BREAKPOINTS_GAP_PX,
  selectMCCSelectedFrameId,
  selectMCCFramesForAllBreakpoints,
  selectMCCRevealedFrameIds,
  selectMCCGuardrailsPolicyType,
  getGuardrailPolicy,
  selectMCCWouldRevealFrameExceedBudget,
  mccFrameSelected,
  mccFrameWidthChanged,
  mccFrameContentHidden,
  mccFrameContentRevealed,
  pageViewOnOpenCanvasClosed,
  selectCurrentBreakpointFrameId,
} from '@packages/systems/multi-context-canvas/store';
import type {
  MCCRevealButtonTooltip,
  PageFrame,
} from '@packages/systems/multi-context-canvas/store';
import {
  selectViewport,
  selectViewportScale,
} from '@packages/systems/open-canvas/store';
import {
  focusOpenCanvasSink,
  getIsShowingInactiveToolbars,
} from '@packages/systems/open-canvas/core';
import {MCC_TOOLBAR_VISIBILITY_ZOOM_LIMIT} from '../open-canvas-config/constants';
import {weakMemo} from '@packages/utilities/memo';
import {
  FrameUI,
  getBreakpointWidthRange,
  getFrameDOMElementById,
  useFrameResizeCallbacks,
  useFrameSizeTransition,
  useFrameSizeTransitionControl,
  useFrameAccent,
} from '@packages/systems/open-canvas/ui';
import type {BreakpointID, BreakpointsMap} from '@packages/systems/style/types';
import type {Breakpoint} from '@packages/systems/style/media-queries';
import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
  LARGER_BREAKPOINT_IDS,
} from '@packages/systems/style/breakpoint-ids';
import BreakpointsIcons from '@packages/systems/ui/shared-ui/BreakpointsIcons';
import {getBreakpointWording} from '@packages/systems/style/breakpoints-config';
import {
  ExpandIcon,
  HideIcon,
  IconButton,
  ShowIcon,
  DeviceLaptopIcon,
  View,
} from '@packages/systems/spring';
import {BaseBadge} from '../BaseBadge';
import {emotionStyled} from '@packages/utilities/styled';
import {
  MCC_FRAME_EXIT_BUTTON_AUTOMATION_ID,
  MCC_FRAME_TOOLBAR_AUTOMATION_ID,
  MCC_FRAME_TOOLBAR_SELECTOR,
  getVisibleToolbarActionButtons,
  getAdjacentMCCFrameToolbarAction,
} from './mccFrameToolbarActions';
import {RevealFrameConfirmationPopover} from '../MCCGuardrailsUI';

const selectBreakpoints = (state: DesignerStores) =>
  state.StyleBlockStore.breakpoints;

const selectIsEditingGrid = (state: DesignerStores) =>
  state.CssGridUiStore.editingGrid;

const mkGetViewportScale = weakMemo(
  (flux: {getState: () => Parameters<typeof selectViewportScale>[0]}) => () =>
    selectViewportScale(flux.getState())
);

/**
 * Renders the frames UI for the Multi Context Canvas.
 * Currently this is a single frame inside a grid.
 */
export const MultiContextCanvasFramesRenderer = () => {
  const flux = useFluxContext<DesignerFlux>();
  const dispatch = useDispatch();
  const viewport = useDesignerSelector(selectViewport);
  const currentFrameId = useDesignerSelector(selectCurrentBreakpointFrameId);
  const selectedFrameId = useDesignerSelector(selectMCCSelectedFrameId);
  const breakpoints = useDesignerSelector(selectBreakpoints);
  const isEditingGrid = useDesignerSelector(selectIsEditingGrid);

  const allFrames = useDesignerSelector(selectMCCFramesForAllBreakpoints);
  const revealedFrameIds = useDesignerSelector(selectMCCRevealedFrameIds);
  const guardrailsPolicyType = useDesignerSelector(
    selectMCCGuardrailsPolicyType
  );

  const guardrailPolicy =
    guardrailsPolicyType && getGuardrailPolicy(guardrailsPolicyType);
  const revealButtonTooltip = guardrailPolicy
    ? guardrailPolicy.revealButtonTooltip
    : null;
  const wouldRevealFrameExceedBudget = useDesignerSelector(
    selectMCCWouldRevealFrameExceedBudget
  );
  const isShowingInactiveToolbars = getIsShowingInactiveToolbars(
    viewport,
    MCC_TOOLBAR_VISIBILITY_ZOOM_LIMIT
  );

  const getViewportScale = mkGetViewportScale(flux);

  const onMCCClose = React.useCallback(() => {
    dispatch(pageViewOnOpenCanvasClosed({source: 'Frame expand button'}));
  }, [dispatch]);

  if (allFrames.length === 0) return null;

  return (
    <FrameUI.FrameGrid
      data-automation-id="mcc-frame-tools-grid-container"
      orientation="horizontal"
      gap={MCC_ALL_BREAKPOINTS_GAP_PX}
      viewport={viewport}
    >
      {allFrames.map((frame) => {
        const isFrameActive = frame.id === currentFrameId;
        const isFrameSelected = frame.id === selectedFrameId;
        const isContentRevealed = Boolean(revealedFrameIds[frame.id]);
        const isToolbarHidden = getIsMCCFrameToolbarHidden({
          isShowingInactiveToolbars,
          isFrameActive,
          isFrameSelected,
          isContentRevealed,
        });

        return (
          <MCCBreakpointFrame
            key={frame.id}
            frame={frame}
            isActive={isFrameActive}
            isSelected={isFrameSelected}
            isContentRevealed={isContentRevealed}
            isSelectable={!isEditingGrid}
            isHidden={isToolbarHidden}
            revealButtonTooltip={revealButtonTooltip}
            shouldConfirmReveal={
              !isContentRevealed && wouldRevealFrameExceedBudget
            }
            breakpoints={breakpoints}
            getViewportScale={getViewportScale}
            onMCCClose={onMCCClose}
          />
        );
      })}
    </FrameUI.FrameGrid>
  );
};

/**
 * Fallback reveal-button tooltip when the active policy has no perf
 * messaging (low complexity). Matches the hidden-content icon state.
 */
const REVEAL_BUTTON_DEFAULT_TOOLTIP = 'Content hidden';

type MCCFrameToolbarVisibility = {
  isShowingInactiveToolbars: boolean;
  isFrameActive: boolean;
  isFrameSelected: boolean;
  isContentRevealed: boolean;
};

const getIsMCCFrameToolbarHidden = ({
  isShowingInactiveToolbars,
  isFrameActive,
  isFrameSelected,
  isContentRevealed,
}: MCCFrameToolbarVisibility) =>
  isContentRevealed &&
  !isShowingInactiveToolbars &&
  !isFrameActive &&
  !isFrameSelected;

type MCCBreakpointFrameProps = {
  frame: PageFrame;
  isActive: boolean;
  isSelected: boolean;
  isContentRevealed: boolean;
  isSelectable: boolean;
  isHidden: boolean;
  revealButtonTooltip?: MCCRevealButtonTooltip | null;
  shouldConfirmReveal: boolean;
  breakpoints: BreakpointsMap<Breakpoint> | null;
  getViewportScale: () => number;
  onMCCClose: () => void;
};

/**
 * Wires per-frame callbacks and width clamping for a single breakpoint frame.
 */
const MCCBreakpointFrame = React.memo(function MCCBreakpointFrame({
  frame,
  isActive,
  isSelected,
  isContentRevealed,
  isSelectable,
  isHidden,
  revealButtonTooltip,
  shouldConfirmReveal,
  breakpoints,
  getViewportScale,
  onMCCClose,
}: MCCBreakpointFrameProps) {
  const flux = useFluxContext<DesignerFlux>();
  const dispatch = useDispatch();

  const widthRange = React.useMemo(
    () => getBreakpointWidthRange(breakpoints, frame.breakpointId),
    [breakpoints, frame.breakpointId]
  );

  const clampValue = React.useCallback(
    (_dimension: 'width' | 'height', value: number) =>
      Math.max(widthRange.min, Math.min(widthRange.max, value)),
    [widthRange]
  );

  const onClick = React.useCallback(() => {
    if (flux.getState().CssGridUiStore.editingGrid) return;
    dispatch(mccFrameSelected({frameId: frame.id}));
  }, [dispatch, flux, frame.id]);

  const onCommit = React.useCallback(
    (dimension: 'width' | 'height', value: number) => {
      if (dimension !== 'width') return;
      dispatch(mccFrameWidthChanged({frameId: frame.id, width: value}));
    },
    [dispatch, frame.id]
  );

  const onFrameHandleDoubleClick = React.useCallback(
    (frameId: string) => {
      dispatch(mccFrameWidthChanged({frameId, width: null}));
    },
    [dispatch]
  );

  const onRevealContent = React.useCallback(() => {
    dispatch(mccFrameContentRevealed({frameId: frame.id, source: 'canvas'}));
  }, [dispatch, frame.id]);

  const onHideContent = React.useCallback(() => {
    dispatch(mccFrameContentHidden({frameId: frame.id, source: 'canvas'}));
  }, [dispatch, frame.id]);

  return (
    <MCCFrame
      frameId={frame.id}
      breakpointId={frame.breakpointId}
      width={frame.width}
      isActive={isActive}
      isSelected={isSelected}
      isContentRevealed={isContentRevealed}
      isSelectable={isSelectable}
      isHidden={isHidden}
      revealButtonTooltip={revealButtonTooltip}
      shouldConfirmReveal={shouldConfirmReveal}
      onClick={onClick}
      getFrameDOMElement={getFrameDOMElementById}
      getViewportScale={getViewportScale}
      onCommit={onCommit}
      onFrameHandleDoubleClick={onFrameHandleDoubleClick}
      clampValue={clampValue}
      onMCCClose={onMCCClose}
      onHideContent={onHideContent}
      onRevealContent={onRevealContent}
    />
  );
});

/**
 * Similar to `getBreakpointLabel` but uses "and above" as the suffix for larger
 * breakpoints. This is the wording used for Breakpoint Frame labeling in
 * the Breakpoints list and MCC frame headers.
 */
export const getBreakpointLabelForFrame = (breakpointId: BreakpointID) => {
  if (LARGER_BREAKPOINT_IDS.includes(breakpointId)) {
    return `${getBreakpointWording(breakpointId).label} and above`;
  }

  return getBreakpointWording(breakpointId).label;
};

const BREAKPOINT_ID_TO_FRAME_LABEL: Record<BreakpointID, string> = {
  [BREAKPOINT_ID_XXL]: getBreakpointLabelForFrame(BREAKPOINT_ID_XXL),
  [BREAKPOINT_ID_XL]: getBreakpointLabelForFrame(BREAKPOINT_ID_XL),
  [BREAKPOINT_ID_LARGE]: getBreakpointLabelForFrame(BREAKPOINT_ID_LARGE),
  [BREAKPOINT_ID_MAIN]: getBreakpointLabelForFrame(BREAKPOINT_ID_MAIN),
  [BREAKPOINT_ID_MEDIUM]: getBreakpointLabelForFrame(BREAKPOINT_ID_MEDIUM),
  [BREAKPOINT_ID_SMALL]: getBreakpointLabelForFrame(BREAKPOINT_ID_SMALL),
  [BREAKPOINT_ID_TINY]: getBreakpointLabelForFrame(BREAKPOINT_ID_TINY),
};

type MCCFrameProps = {
  frameId: string;
  breakpointId: BreakpointID;
  width: number;
  isActive: boolean;
  isSelected: boolean;
  /**
   * Whether this frame's page content is mounted. When false, the frame
   * body renders a placeholder and the toolbar offers a reveal control.
   */
  isContentRevealed: boolean;
  /**
   * When false (e.g. while editing a Grid element), the frame ignores
   * click-to-select and does not show the hover highlight.
   */
  isSelectable: boolean;
  isHidden: boolean;
  /**
   * Tooltip for the reveal button, from the active guardrails policy.
   * Absent (low complexity) falls back to a plain state tooltip without
   * the perf messaging.
   */
  revealButtonTooltip?: MCCRevealButtonTooltip | null;
  shouldConfirmReveal: boolean;
  onClick: () => void;
  getFrameDOMElement: (frameId: string) => HTMLElement | null;
  getViewportScale: () => number;
  onCommit: (dimension: 'width' | 'height', value: number) => void;
  onFrameHandleDoubleClick: (frameId: string) => void;
  clampValue: (dimension: 'width' | 'height', value: number) => number;
  onMCCClose: () => void;
  onHideContent?: () => void;
  onRevealContent: () => void;
};

/**
 * An interactive frame toolbar for the Multi Context Canvas.
 * Displays the breakpoint name and the current frame width.
 * Supports hover (blue border) and click-to-select behavior.
 */
const MCCFrame = React.memo(
  ({
    frameId,
    breakpointId,
    width,
    isActive,
    isSelected,
    isContentRevealed,
    isSelectable,
    isHidden,
    revealButtonTooltip,
    shouldConfirmReveal,
    onClick,
    getFrameDOMElement,
    getViewportScale,
    onCommit,
    onFrameHandleDoubleClick,
    clampValue,
    onMCCClose,
    onHideContent,
    onRevealContent,
  }: MCCFrameProps) => {
    const frameSizeTransition = useFrameSizeTransition(frameId);
    const frameSizeTransitionControl = useFrameSizeTransitionControl(frameId);
    const frameWidth = frameSizeTransition?.width ?? width;
    const BreakpointIcon = BreakpointsIcons[breakpointId];
    const isBaseBreakpoint = breakpointId === BREAKPOINT_ID_MAIN;
    const breakpointLabel = BREAKPOINT_ID_TO_FRAME_LABEL[breakpointId];

    const {onFrameHandleDrag, onFrameHandleDragEnd} = useFrameResizeCallbacks({
      frameId,
      getViewportScale,
      frameSizeTransitionControl,
      onCommit,
      clampValue,
    });

    const accent = useFrameAccent();
    const colorVariant = accent === 'blue' ? 'primary' : accent;

    const [isHovered, setIsHovered] = React.useState(false);
    const handleMouseEnter = React.useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = React.useCallback(() => setIsHovered(false), []);
    const handleToolbarClick = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (!isSelectable) return;
        // Interactive children (the exit button) manage their own focus; only
        // claim it when the click landed on the toolbar surface itself.
        if (!(event.target as HTMLElement).closest('button')) {
          event.currentTarget.focus({preventScroll: true});
        }
        onClick();
      },
      [onClick, isSelectable]
    );
    const handleHitAreasClick = React.useCallback(() => {
      if (!isSelectable) return;
      onClick();
    }, [onClick, isSelectable]);
    const handleToolbarKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (
          event.currentTarget === event.target &&
          event.key === 'Tab' &&
          !event.shiftKey
        ) {
          const nextFocusedElement = getVisibleToolbarActionButtons(
            event.currentTarget
          )[0];
          if (!nextFocusedElement) return;

          event.preventDefault();
          nextFocusedElement.focus({preventScroll: true});
        }
      },
      []
    );
    // Tab between toolbar actions must stay on preventScroll: native focus can
    // scroll an off-viewport frame into view and desync canvas ↔ tools. Button
    // order is discovered from the DOM so new actions join the chain without
    // hardcoded refs.
    const handleToolbarActionKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        const button = event.currentTarget;
        const toolbar = button.closest<HTMLElement>(MCC_FRAME_TOOLBAR_SELECTOR);

        if (event.key === 'Escape') {
          // Escape releases the Tab chain without closing the canvas:
          // focus returns to the frame toolbar.
          toolbar?.focus({preventScroll: true});
          return;
        }

        if (event.key !== 'Tab') return;

        const direction = event.shiftKey ? -1 : 1;
        const adjacentAction = getAdjacentMCCFrameToolbarAction(
          button,
          direction
        );
        if (adjacentAction) {
          event.preventDefault();
          event.stopPropagation();
          adjacentAction.focus({preventScroll: true});
          return;
        }

        // No adjacent toolbar action in this frame or any revealed neighbour.
        // Shift+Tab returns to this frame's toolbar; Tab hands off to the
        // neutral focus sink rather than crossing into the canvas iframe.
        if (event.shiftKey) {
          if (!toolbar) return;

          event.preventDefault();
          event.stopPropagation();
          toolbar.focus({preventScroll: true});
          return;
        }

        if (!focusOpenCanvasSink(button.ownerDocument)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
      },
      []
    );
    const revealTooltipProps = revealButtonTooltip
      ? {
          title: revealButtonTooltip.title,
          label: revealButtonTooltip.description,
        }
      : {label: REVEAL_BUTTON_DEFAULT_TOOLTIP};
    const handleHideContentClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onHideContent?.();
      },
      [onHideContent]
    );
    const handleRevealContentClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onRevealContent();
      },
      [onRevealContent]
    );

    const maybeHiddenContentOverlay = !isContentRevealed ? (
      <HiddenContentOverlay
        data-automation-id={`mcc-frame-hidden-overlay-${breakpointId}`}
      />
    ) : null;
    const exitButton = (
      <ToolbarActionWrapper>
        <IconButton
          Icon={ExpandIcon}
          label="Exit pan & zoom canvas"
          onClick={onMCCClose}
          onKeyDown={handleToolbarActionKeyDown}
          variant="ghost"
          colorVariant={isSelected ? accent : undefined}
          data-automation-id={MCC_FRAME_EXIT_BUTTON_AUTOMATION_ID}
        />
      </ToolbarActionWrapper>
    );

    if (isHidden) {
      return (
        <FrameUI.Frame
          frameId={frameId}
          getFrameDOMElement={getFrameDOMElement}
          data-automation-id="mcc-frame-toolbar-view"
        >
          {maybeHiddenContentOverlay}
        </FrameUI.Frame>
      );
    }

    return (
      <FrameUI.Frame
        frameId={frameId}
        getFrameDOMElement={getFrameDOMElement}
        data-automation-id="mcc-frame-toolbar-view"
        isSelectable={isSelectable}
        isActive={isActive}
        isSelected={isSelected}
      >
        <FrameUI.Outline
          data-automation-id="mcc-frame-toolbar-view-outline"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <FrameUI.HitAreas onClick={handleHitAreasClick} />
          <MCCFrameToolbar
            data-automation-id={MCC_FRAME_TOOLBAR_AUTOMATION_ID}
            onClick={handleToolbarClick}
            onKeyDown={handleToolbarKeyDown}
            tabIndex={-1}
          >
            <FrameUI.ToolbarLeft>
              <BreakpointIconWrapper
                isSelected={isSelected}
                data-automation-id={`mcc-frame-breakpoint-icon-${breakpointId}`}
              >
                {breakpointId === BREAKPOINT_ID_MAIN ? (
                  <DeviceLaptopIcon size="small" colorVariant="inherit" />
                ) : (
                  <BreakpointIcon size="small" colorVariant="inherit" />
                )}
              </BreakpointIconWrapper>
              <BreakpointLabelWrapper>
                <FrameUI.Label
                  label={breakpointLabel}
                  isSelected={isSelected}
                  data-automation-id="mcc-frame-breakpoint-label"
                />
              </BreakpointLabelWrapper>
              {isBaseBreakpoint && (
                <FrameBaseBadge
                  data-automation-id="mcc-frame-base-badge"
                  density="compact"
                  isRounded
                  colorVariant={isSelected ? colorVariant : 'default'}
                />
              )}
            </FrameUI.ToolbarLeft>
            <FrameUI.ToolbarRight>
              <ToolbarRightRevealArea
                data-revealed={isActive || isSelected || isHovered}
                data-automation-id="mcc-frame-toolbar-right-reveal-area"
              >
                {isContentRevealed ? (
                  <>
                    <EditableFrameWidthLabel
                      frameWidth={frameWidth}
                      breakpointLabel={breakpointLabel}
                      isSelected={isSelected}
                      isSelectable={isSelectable}
                      onFrameClick={onClick}
                      onCommit={onCommit}
                      clampValue={clampValue}
                    />
                    {onHideContent ? (
                      <ToolbarActionWrapper>
                        <IconButton
                          Icon={ShowIcon}
                          label={`Hide ${breakpointLabel} content`}
                          onClick={handleHideContentClick}
                          onKeyDown={handleToolbarActionKeyDown}
                          variant="ghost"
                          colorVariant={isSelected ? accent : undefined}
                          withTooltip
                          data-automation-id={`mcc-frame-hide-button-${breakpointId}`}
                        />
                      </ToolbarActionWrapper>
                    ) : null}
                    {exitButton}
                  </>
                ) : (
                  <>
                    <ToolbarActionWrapper>
                      <RevealFrameConfirmationPopover
                        isRequired={shouldConfirmReveal}
                        onReveal={onRevealContent}
                        placement="bottom-end"
                      >
                        <IconButton
                          Icon={HideIcon}
                          label={`Reveal ${breakpointLabel} content`}
                          onClick={
                            shouldConfirmReveal
                              ? undefined
                              : handleRevealContentClick
                          }
                          onKeyDown={handleToolbarActionKeyDown}
                          variant="ghost"
                          colorVariant={isSelected ? accent : undefined}
                          withTooltip
                          tooltipProps={revealTooltipProps}
                          data-automation-id={`mcc-frame-reveal-button-${breakpointId}`}
                        />
                      </RevealFrameConfirmationPopover>
                    </ToolbarActionWrapper>
                    {exitButton}
                  </>
                )}
              </ToolbarRightRevealArea>
            </FrameUI.ToolbarRight>
          </MCCFrameToolbar>
        </FrameUI.Outline>
        {isSelected && (
          <FrameUI.ResizeHandle
            dimension="width"
            onDrag={onFrameHandleDrag}
            onDragEnd={onFrameHandleDragEnd}
            onDoubleClick={() => onFrameHandleDoubleClick(frameId)}
          />
        )}
        {maybeHiddenContentOverlay}
        {isBaseBreakpoint && (
          <FrameUI.BaseIndicator
            isSelected={isSelected}
            tooltipTitle="Base breakpoint"
            tooltipLabel="All breakpoints inherit styles from the base."
            data-automation-id="mcc-frame-base-asterisk-indicator"
            tabIndex={-1}
          />
        )}
      </FrameUI.Frame>
    );
  }
);

type EditableFrameWidthLabelProps = {
  frameWidth: number;
  breakpointLabel: string;
  isSelected: boolean;
  isSelectable: boolean;
  onFrameClick: () => void;
  onCommit: MCCFrameProps['onCommit'];
  clampValue: MCCFrameProps['clampValue'];
};

const EditableFrameWidthLabel = ({
  frameWidth,
  breakpointLabel,
  isSelected,
  isSelectable,
  onFrameClick,
  onCommit,
  clampValue,
}: EditableFrameWidthLabelProps) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftWidth, setDraftWidth] = React.useState(String(frameWidth));
  const isEditingRef = React.useRef(false);
  const shouldRestoreToolbarFocusRef = React.useRef(false);
  const widthInputRef = React.useRef<HTMLInputElement>(null);
  const toolbarElementRef = React.useRef<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    if (isEditing) {
      widthInputRef.current?.focus({preventScroll: true});
      widthInputRef.current?.select();
      return;
    }

    if (shouldRestoreToolbarFocusRef.current) {
      shouldRestoreToolbarFocusRef.current = false;
      toolbarElementRef.current?.focus({preventScroll: true});
    }
  }, [isEditing]);

  const finishEditing = React.useCallback(
    (commit: boolean, restoreToolbarFocus = false) => {
      if (!isEditingRef.current) return;

      isEditingRef.current = false;
      if (commit) {
        const rawValue = (widthInputRef.current?.value ?? draftWidth)
          .trim()
          .replace(/px$/i, '')
          .trim();
        const value = rawValue === '' ? NaN : Number(rawValue);
        if (Number.isFinite(value)) {
          onCommit('width', clampValue('width', Math.round(value)));
        }
      }

      shouldRestoreToolbarFocusRef.current = restoreToolbarFocus;
      setIsEditing(false);
    },
    [clampValue, draftWidth, onCommit]
  );

  const startEditing = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isSelectable) return;

      event.stopPropagation();
      toolbarElementRef.current = event.currentTarget.closest(
        MCC_FRAME_TOOLBAR_SELECTOR
      );
      onFrameClick();
      isEditingRef.current = true;
      setDraftWidth(String(frameWidth));
      setIsEditing(true);
    },
    [frameWidth, isSelectable, onFrameClick]
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        finishEditing(true, true);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        finishEditing(false, true);
        return;
      }

      if (event.key !== 'Tab') return;

      event.preventDefault();
      finishEditing(true);
      const toolbar =
        event.currentTarget.closest<HTMLElement>(MCC_FRAME_TOOLBAR_SELECTOR) ??
        toolbarElementRef.current;
      if (!toolbar) return;

      if (event.shiftKey) {
        toolbar.focus({preventScroll: true});
        return;
      }

      getVisibleToolbarActionButtons(toolbar).at(0)?.focus({
        preventScroll: true,
      });
    },
    [finishEditing]
  );

  /**
   * The reason we want to stop propagation here is because otherwise this click
   * event will be captured by the component above (MCCFrame) and will execute
   * `handleToolbarClick`, moving the focus away from the input and making it
   * impossible for the customer to reposition the caret.
   */
  const handleInputWrapperClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    []
  );

  return (
    <WidthLabelWrapper>
      {isEditing ? (
        <WidthInputWrapper onClick={handleInputWrapperClick}>
          <WidthInput
            ref={widthInputRef}
            aria-label={`${breakpointLabel} frame width in pixels`}
            data-automation-id="mcc-frame-width-input"
            inputMode="numeric"
            value={draftWidth}
            onBlur={() => finishEditing(true)}
            onChange={(event) => {
              setDraftWidth(event.target.value);
            }}
            onKeyDown={handleInputKeyDown}
          />
          <WidthUnitSuffix aria-hidden="true">px</WidthUnitSuffix>
        </WidthInputWrapper>
      ) : (
        <WidthLabelButton
          aria-label={`Edit ${breakpointLabel} frame width, ${frameWidth} pixels`}
          data-automation-id="mcc-frame-width-label"
          onClick={startEditing}
          tabIndex={-1}
          type="button"
        >
          <FrameUI.Label
            label={`${frameWidth}px`}
            isSelected={isSelected}
            weight="normal"
          />
        </WidthLabelButton>
      )}
    </WidthLabelWrapper>
  );
};

const FrameBaseBadge = emotionStyled(BaseBadge)({
  [`@container ${FrameUI.CONTAINER_NAME} (max-width: 200px)`]: {
    display: 'none',
  },
});

const BreakpointIconWrapper = emotionStyled(FrameUI.Icon)({
  flexShrink: 0,
});

const BreakpointLabelWrapper = emotionStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  minWidth: 0,

  [`@container ${FrameUI.CONTAINER_NAME} (max-width: 80px)`]: {
    display: 'none',
  },
});

const WidthLabelWrapper = emotionStyled('div')({
  display: 'flex',
  [`@container ${FrameUI.CONTAINER_NAME} (max-width: 150px)`]: {
    display: 'none',
  },
});

const WidthLabelButton = emotionStyled('button')({
  appearance: 'none',
  background: 'none',
  border: 0,
  color: 'inherit',
  cursor: 'text',
  font: 'inherit',
  padding: 0,
});

const WIDTH_INPUT_WRAPPER_WIDTH = 50;

const WidthInputWrapper = emotionStyled('div')({
  backgroundColor: '$uiInputBackground',
  border: '$border2',
  borderRadius: '$2',
  boxShadow: '$inputInner',
  color: '$textPrimary',
  display: 'flex',

  // The magic-est of numbers: we are doing a weird number here because
  // we want the input to perfectly match the position of the underlying
  // width label, so the transition from non-editable to editable is
  // seamless to the eye.
  marginRight: '-0.3rem',
  minWidth: 0,
  width: WIDTH_INPUT_WRAPPER_WIDTH,
  '&:focus-within': {
    borderColor: '$blueBorder',
  },
});

const WidthInput = emotionStyled('input')({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  flex: 1,
  fontFamily: '$sansSerif',
  fontSize: '$body',
  fontWeight: '$normal',
  lineHeight: '$body',
  minWidth: 0,
  outline: 'none',
  padding: '0 0 0 $0.5',
  textAlign: 'right',
});

const WidthUnitSuffix = emotionStyled('span')({
  alignItems: 'center',
  color: '$textSecondary',
  display: 'flex',
  fontFamily: '$sansSerif',
  fontSize: '$body',
  fontWeight: '$normal',
  lineHeight: '$body',
  paddingRight: '$1',
  pointerEvents: 'none',
  userSelect: 'none',
});

const ToolbarActionWrapper = emotionStyled('div')({
  [`@container ${FrameUI.CONTAINER_NAME} (max-width: 120px)`]: {
    display: 'none',
  },
});

/**
 * Hides the right-side toolbar contents (width label and actions) when the
 * frame is neither active nor selected nor hovered.
 */
const ToolbarRightRevealArea = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '$1',
  visibility: 'hidden',
  '&[data-revealed="true"]': {
    visibility: 'visible',
  },
});

/**
 * Handles revealing right-side toolbar contents while focus is inside the
 * toolbar.
 */
const MCCFrameToolbar = emotionStyled(FrameUI.Toolbar)({
  [`&:focus-within ${ToolbarRightRevealArea}`]: {
    visibility: 'visible',
  },
});

/**
 * Passive full-frame placeholder shown over a breakpoint frame whose content
 * is hidden. Rendered inside `FrameUI.Frame`, so it is sized and positioned
 * to the frame's measured box. `pointerEvents: 'none'` keeps wheel (zoom)
 * and pan gestures passing through to the canvas. The reveal control lives
 * in the frame toolbar (see `MCCFrame`).
 *
 * The overlay only draws the translucent `$backgroundRaised` tint via
 * `::after`, layered directly over the frame body. The overlay intentionally does
 * not have `backgroundColor` or `border` so that sub-pixel artifacts don't appear between the overlay's box and the frame body at fractional zoom levels.
 */
const HiddenContentOverlay = emotionStyled(View)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: '$backgroundRaised',
    outline: '1px solid $backgroundRaised',
    pointerEvents: 'none',
  },
});

export const forTestSuite = {
  getIsMCCFrameToolbarHidden,
  MCCFrame,
};
